import { InputSummaryItem } from '../../../../input-summary';
import InputSummaryUtils from '../../../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../../../data-structures/unit-value.model';

/**
 * An interface used to define the class structure for the feedstock BMP potential nutrient model
 */
export interface FeedstockBmpPotentialNutrientInterface {
  carbohydrate: UnitValue;
  lipidFat: UnitValue;
  protein: UnitValue;
  biodegradability: UnitValue;
}

/**
 * A class that contains members for the feedstock BMP potential nutrient model
 */
export class FeedstockBmpPotentialNutrient {
  carbohydrate: UnitValue;
  lipidFat: UnitValue;
  protein: UnitValue;
  biodegradability: UnitValue;

  /**
   * constructs the FeedstockBmpPotentialNutrition class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockBmpPotentialNutrientInterface = {
    carbohydrate: new UnitValue(),
    lipidFat: new UnitValue(),
    protein: new UnitValue(),
    biodegradability: new UnitValue(),
  }) {
    this.carbohydrate = new UnitValue(param.carbohydrate);
    this.lipidFat = new UnitValue(param.lipidFat);
    this.protein = new UnitValue(param.protein);
    this.biodegradability = new UnitValue(param.biodegradability);
  }

  /**
   * returns a separate instance of the provided FeedstockBmpPotentialNutrient obj
   * @param feedstockBmpPotentialNutrient will be returned as a separate instance
   */
  static deepCopy(feedstockBmpPotentialNutrient: FeedstockBmpPotentialNutrient) {
    let copy;
    if (feedstockBmpPotentialNutrient) {
      copy = new FeedstockBmpPotentialNutrient(feedstockBmpPotentialNutrient);
      return copy;
    }
    throw new Error('unable to perform deep copy of FeedstockBmpPotentialNutrient');
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each member of the FeedstockBmpPotentialNutrition class
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialNutritionPropName.carbohydrate, this.carbohydrate, [...path, 'carbohydrate']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialNutritionPropName.lipidFat, this.lipidFat, [...path, 'lipidFat']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialNutritionPropName.protein, this.protein, [...path, 'protein']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialNutritionPropName.biodegradability, this.biodegradability, [...path, 'biodegradability']);
    return inputs;
  }
}

/**
 * An enum that contains a proper display name for properties of the FeedstockBmpPotentialNutrition class.
 *
 * ALL MEMBERS ARE INTENTIONALLY CAMELCASE FOR OBJ REFERENCING
 */
export enum FeedstockBmpPotentialNutritionPropName {
  carbohydrate = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.NUTRIENT.CARBOHYDRATE',
  lipidFat = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.NUTRIENT.LIPID_FAT',
  protein = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.NUTRIENT.PROTEIN',
  biodegradability = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.BIODEGRADABILITY',
  id_carbohydrate = 'carbohydrate (total)',
  id_lipidFat = 'lipid/fat (total)',
  id_protein = 'protein (crude)',
  id_biodegradability = 'biodegradability',
}
